// WbLockManagementPage — /weighbridge/settings/locks.
//
// Manager/admin view of every active ticket lock (migration 167), with a
// force-release for stuck locks (e.g. a crashed kiosk session). The list polls
// every 30s so it stays live; expired locks are purged server-side on read.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Unlock } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbLockManagementPage.module.css';

interface TicketLockListItem {
  readonly enc_id: string;
  readonly ticket_number: string | null;
  readonly locked_by: number;
  readonly locked_by_name: string;
  readonly origin: 'kiosk' | 'main';
  readonly acquired_at: string;
  readonly expires_at: string;
  readonly is_expired: boolean;
}

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString([], {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export function WbLockManagementPage(): JSX.Element {
  const queryClient = useQueryClient();
  const locksQuery = useQuery<TicketLockListItem[]>({
    queryKey: ['wb', 'locks'],
    queryFn: async () => {
      const res = await apiClient.get<TicketLockListItem[]>(endpoints.wb.locks);
      return res.data;
    },
    refetchInterval: 30_000,
  });
  const locks = locksQuery.data ?? [];

  const [releaseTarget, setReleaseTarget] = useState<TicketLockListItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const releaseMut = useMutation<void, unknown, string>({
    mutationFn: async (encId: string) => {
      await apiClient.delete(endpoints.wb.lockForceRelease(encId));
    },
    onSuccess: () => {
      setReleaseTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['wb', 'locks'] });
    },
    onError: (err) => {
      setActionError(extractErrorMessage(err) ?? 'Failed to release the lock.');
    },
  });

  return (
    <div className={styles.page}>
      <PageHeader
        title="Lock Management"
        subtitle="View and release active ticket locks"
      />

      {locksQuery.isError ? (
        <p className={styles.error}>{extractErrorMessage(locksQuery.error)}</p>
      ) : null}
      {actionError ? <p className={styles.error}>{actionError}</p> : null}

      <Card flush>
        {locksQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading locks" />
          </div>
        ) : locks.length === 0 ? (
          <EmptyState
            icon={Lock}
            title="No active locks"
            description="No tickets are currently locked. Locks appear here while a staff member has a ticket open for editing."
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Locked by</th>
                <th>Origin</th>
                <th>Since</th>
                <th>Expires</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {locks.map((l) => (
                <tr key={l.enc_id} data-testid={`wb-lock-row-${l.enc_id}`}>
                  <td>{l.ticket_number ?? <span className={styles.muted}>Draft</span>}</td>
                  <td>{l.locked_by_name}</td>
                  <td>
                    <Badge variant={l.origin === 'kiosk' ? 'info' : 'muted'}>
                      {l.origin === 'kiosk' ? 'Kiosk' : 'Main'}
                    </Badge>
                  </td>
                  <td>{fmtTime(l.acquired_at)}</td>
                  <td>{fmtTime(l.expires_at)}</td>
                  <td className={styles.actionsCell}>
                    <Button
                      variant="danger"
                      size="sm"
                      icon={Unlock}
                      onClick={() => {
                        setActionError(null);
                        setReleaseTarget(l);
                      }}
                      data-testid={`wb-lock-release-${l.enc_id}`}
                    >
                      Force release
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <ConfirmModal
        open={releaseTarget !== null}
        title="Force release lock?"
        message={
          releaseTarget
            ? `Release the lock held by ${releaseTarget.locked_by_name} on ${
                releaseTarget.ticket_number ?? 'this draft ticket'
              }? They will lose their edit session.`
            : ''
        }
        confirmLabel="Release"
        variant="danger"
        loading={releaseMut.isPending}
        onConfirm={() => {
          if (releaseTarget) releaseMut.mutate(releaseTarget.enc_id);
        }}
        onCancel={() => setReleaseTarget(null)}
      />
    </div>
  );
}
